import React from 'react';

const IDENTITY_LABELS = {
  completedOn: 'Date',
  nhsNumber: 'NHS number',
  patientName: 'Patient name',
  dateOfBirth: 'Date of birth',
  practiceName: 'GP practice',
  raiserName: "GP's name",
  practicePhone: 'Practice telephone',
  practiceEmail: 'Generic practice email',
  gmcNumber: 'GP GMC number',
  odsCode: 'Practice code',
  reporterName: 'Reporter name (if not the GP)',
};

export function Note({ text, warning }) {
  if (!text) return null;
  return <p className={`qa-note${warning ? ' qa-note-warning' : ''}`}>{text}</p>;
}

function columnClass(groups) {
  if (!groups?.length || groups.length < 2) return '';
  const labels = groups.flatMap((group) => (group.options || []).map((option) => option.label.length));
  const short = labels.filter((length) => length < 34).length;
  const count = groups.length >= 5 && short / labels.length > 0.7 ? 3 : 2;
  return `qa-choice-columns cols-${count}`;
}

export function SingleChoice({ label, options, groups, other, value, onChange, readOnly }) {
  const selected = value?.id || '';
  const choiceGroups = groups?.length ? groups : [{ label: '', options: options || [] }];
  const flat = choiceGroups.flatMap((group) => group.options || []);
  const guidance = flat.find((option) => option.id === selected)?.guidance;
  return (
    <fieldset className="qa-field">
      <legend>{label}</legend>
      <div className={columnClass(groups)}>
      {choiceGroups.map((group) => (
        <div key={group.label || 'options'} className="qa-choice-group">
          {group.label ? <div className="qa-group-label">{group.label}</div> : null}
          {(group.options || []).map((option) => (
            <button
              key={option.id}
              type="button"
              className={`method-button${selected === option.id ? ' selected' : ''}`}
              disabled={readOnly}
              onClick={() => onChange({ id: option.id, other: value?.other || '' })}
            >
              {option.label}
            </button>
          ))}
        </div>
      ))}
      </div>
      {other && selected === 'other' ? (
        <input
          className="qa-other"
          value={value?.other || ''}
          placeholder="Specify"
          disabled={readOnly}
          onChange={(event) => onChange({ id: 'other', other: event.target.value })}
        />
      ) : null}
      {guidance ? <p className="qa-guidance">{guidance}</p> : null}
    </fieldset>
  );
}

export function MultiChoice({ label, groups, other, value, onChange, readOnly }) {
  const selected = new Set(value?.ids || []);
  const choiceGroups = groups || [];
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ids: [...next], other: value?.other || '' });
  };
  return (
    <fieldset className="qa-field">
      <legend>{label}</legend>
      <div className={columnClass(groups)}>
      {choiceGroups.map((group) => (
        <div key={group.label || 'options'} className="qa-choice-group">
          {group.label ? <div className="qa-group-label">{group.label}</div> : null}
          {(group.options || []).map((option) => (
            <button
              key={option.id}
              type="button"
              className={`method-button${selected.has(option.id) ? ' selected' : ''}`}
              disabled={readOnly}
              onClick={() => toggle(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ))}
      </div>
      {other && selected.has('other') ? (
        <input
          className="qa-other"
          value={value?.other || ''}
          placeholder="Specify"
          disabled={readOnly}
          onChange={(event) => onChange({ ids: [...selected], other: event.target.value })}
        />
      ) : null}
    </fieldset>
  );
}

export function FreeText({ label, multiline, value, onChange, readOnly }) {
  if (multiline) {
    return (
      <label className="qa-field qa-text">
        <span>{label}</span>
        <textarea rows={6} value={value || ''} readOnly={readOnly} onChange={(event) => onChange(event.target.value)} />
      </label>
    );
  }
  return (
    <label className="qa-field qa-text">
      <span>{label}</span>
      <input value={value || ''} readOnly={readOnly} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export function IdentityFields({ fields, context, onContext, readOnly }) {
  if (!fields?.length) return null;
  return (
    <div className="qa-identity">
      {fields.map((key) => (
        <label key={key} className="qa-field qa-text">
          <span>{IDENTITY_LABELS[key] || key}</span>
          <input value={context?.[key] || ''} readOnly={readOnly} onChange={(event) => onContext(key, event.target.value)} />
        </label>
      ))}
    </div>
  );
}

export function DeliveryAdvice({ advice, attachmentNote }) {
  if (!advice && !attachmentNote) return null;
  return (
    <div className="qa-advice">
      {advice ? <p>{advice}</p> : null}
      {attachmentNote ? <p>{attachmentNote}</p> : null}
    </div>
  );
}
